import { Controller } from '@hotwired/stimulus';

const MAX_PROPERTIES = 5;
const MULTI_PROPERTY_LABELS = {
  single_property: 'Single Report',
  portfolio_single_report: 'Portfolio: Single Report',
  portfolio_separate_report: 'Portfolio: Separate Reports',
};
const CONVERSION_MODAL_CONTENT = {
  'single_property:portfolio_single_report': {
    title: 'Convert to Portfolio Single Report?',
    message: `You're about to switch this <strong class="font-medium text-black-1000">Single Report</strong> into a <strong class="font-medium text-black-1000">Portfolio - Single Report</strong>. All entered data will be preserved, and existing property details will move into Property №1. Please review all fields after conversion to ensure everything is correct.`,
  },
  'single_property:portfolio_separate_report': {
    title: 'Convert to Portfolio Separate Report?',
    message: `You're about to switch this <strong class="font-medium text-black-1000">Single Report</strong> into a <strong class="font-medium text-black-1000">Portfolio - Separate Reports</strong>. All entered data will be preserved, and existing property details will move into Report №1. Please review all fields after conversion to ensure everything is correct.`,
  },
  'portfolio_single_report:single_property': {
    title: 'Convert to Single Report?',
    message: `You're about to convert this <strong class="font-medium text-black-1000">Portfolio - Single Report</strong> into a <strong class="font-medium text-black-1000">Single Report</strong>. Property №1's information will become the primary Address & Details, and other properties will be archived and can be restored if needed. Please review all fields after conversion.`,
  },
  'portfolio_separate_report:single_property': {
    title: 'Convert to Single Report?',
    message: `You're about to convert this <strong class="font-medium text-black-1000">Portfolio - Separate Reports</strong> into a <strong class="font-medium text-black-1000">Single Report</strong>. Report №1's information will become the primary Address & Details, and other reports will be archived and can be restored if needed. Please review all fields after conversion.`,
  },
  'portfolio_single_report:portfolio_separate_report': {
    title: 'Convert to Portfolio Separate Report?',
    message: `You're about to convert this <strong class="font-medium text-black-1000">Portfolio - Single Report</strong> into a <strong class="font-medium text-black-1000">Portfolio - Separate Reports</strong>. All entered data will be preserved, and existing Properties will be converted into Reports. Please review all fields after conversion to ensure everything is correct.`,
  },
  'portfolio_separate_report:portfolio_single_report': {
    title: 'Convert to Portfolio Single Report?',
    message: `You're about to convert this <strong class="font-medium text-black-1000">Portfolio - Separate Reports</strong> into a <strong class="font-medium text-black-1000">Portfolio - Single Report</strong>. All entered data will be preserved, and existing Reports will be converted into Properties. Please review all fields after conversion to ensure everything is correct.`,
  },
};
const BUTTON_ACTIVE_CLASSES = ['bg-white-1000', 'shadow-[0_0_0_1px_theme(colors.black.100)]', 'hover:bg-lightGray-1000'];
const BUTTON_INACTIVE_CLASSES = ['bg-brand-50', 'shadow-[0_0_0_1px_theme(colors.brand.100)]', 'hover:bg-linear-0', 'hover:from-black-200', 'hover:to-black-200', 'hover:shadow-[0_0_0_1px_theme(colors.brand.200)]'];

export default class extends Controller {
  static targets = [
    'multiPropertyTypeSelect',
    'portfolioSingleReportSection',
    'valuationDetailsSection',
    'reportSection',
    'reportHeader',
    'valuationDetailsButton',
    'reportButton',
    'addMoreButton',
    'reportButtonTemplate',
    'propertyAddressNavButton',
    'propertyAddressSection',
    'propertyDetailsNavButton',
    'propertyDetailsSection',
    'propertySidebarSection',
    'valuationDetailsSidebarSection',
    'propertyTitle',
    'portfolioValueField',
    'propertiesValueSumField',
    'specificPropertyTypeField',
    'multiPropertyDetailsSection',
    'actionsMenu',
    'actionsMenuVariant',
    'actionsMenuContainer',
    'addMoreContainer',
    'addressEntryModeField',
    'additionalAddressesUpload',
    'additionalAddressesInput',
    'additionalAddressesDisplay',
    'additionalAddressesRowTemplate',
    'additionalAddressesSignedIdField',
    'removeAdditionalAddressesFileField',
    'additionalAddressesModal'
  ];
  static values = {
    currentView: { type: String, default: 'valuation-details' }
  };

  connect() {
    const multiPropertyType = this.getCurrentMultiPropertyType();
    this.togglePortfolioSingleReportSection();
    this.toggleTabControls();
    this.togglePortfolioValueFieldsVisibility();
    this.toggleSpecificPropertyTypeVisibility();

    const isSingleProperty = multiPropertyType === 'single_property';
    if (isSingleProperty) {
      this.toggleSidebarSectionsForView('valuation-details', null);
    } else {
      this.showValuationDetails();
    }

    this.initialReportCount = this.reportSectionTargets.length;
    this.updateAddMoreButtonState();
    this.initialiseSyncedPropertyType();
    this.previousMultiPropertyTypeValue = this.getCurrentMultiPropertyType();
    this.isProcessingChange = false;
    this.pendingTimeoutId = null;

    // Ensure property buttons are visible in single property mode after page load
    this.ensureSinglePropertyButtonsVisible();
  }

  disconnect() {
    if (this.pendingTimeoutId) {
      clearTimeout(this.pendingTimeoutId);
      this.pendingTimeoutId = null;
    }

    this.initialReportCount = null;
    this.previousMultiPropertyTypeValue = null;
    this.previousLabel = null;
    this.lastSyncedPropertyTypeValue = null;
    this.isProcessingChange = false;
  }

  ensureSinglePropertyButtonsVisible() {
    const isSingleProperty = this.getCurrentMultiPropertyType() === 'single_property';
    if (!isSingleProperty) return;

    // Query DOM directly since scroll-to-section may not be inside this controller's element
    const propertyButtons = document.querySelectorAll('[data-multi-property-target="propertySidebarSection"][data-report-index="0"]');
    propertyButtons.forEach((btn) => btn.classList.remove('hidden'));
  }

  getCurrentMultiPropertyType() {
    if (!this.hasMultiPropertyTypeSelectTarget) return 'single_property';
    const hiddenField = this.multiPropertyTypeSelectTarget.querySelector('input[type="hidden"][name*="multi_property_type"]');
    return hiddenField?.value || 'single_property';
  }

  setMultiPropertyType(value, label) {
    if (!this.hasMultiPropertyTypeSelectTarget) return;

    const hiddenField = this.multiPropertyTypeSelectTarget.querySelector('input[type="hidden"][name*="multi_property_type"]');
    const labelElement = this.multiPropertyTypeSelectTarget.querySelector('[data-menu-target="label"]');
    const menuElement = this.multiPropertyTypeSelectTarget.querySelector('[data-controller~="menu"]');

    if (hiddenField) hiddenField.value = value;
    if (labelElement && label) labelElement.textContent = label;
    if (menuElement) menuElement.dataset.menuSelectedValueValue = value;
  }

  async onMultiPropertyTypeChange(event) {
    if (this.isProcessingChange) return;

    const newValue = event.detail?.value;
    const previousValue = this.previousMultiPropertyTypeValue;
    if (!newValue || newValue === previousValue) return;

    const newLabel = event.detail?.label || this.getLabelForValue(newValue);

    if (this.needsConfirmation(previousValue, newValue)) {
      await this.handleConfirmationFlow(previousValue, newValue, newLabel);
    } else {
      this.applyMultiPropertyTypeChange(newValue, newLabel);
    }
  }

  needsConfirmation(fromValue, toValue) {
    return fromValue !== toValue;
  }

  async handleConfirmationFlow(previousValue, newValue, newLabel) {
    const previousLabel = this.getPreviousLabel();
    this.setMultiPropertyType(previousValue, previousLabel);

    try {
      const confirmed = await this.showConfirmationModal(previousValue, newValue);

      if (confirmed) {
        this.isProcessingChange = true;
        this.setMultiPropertyType(newValue, newLabel);
        this.applyMultiPropertyTypeChange(newValue, newLabel);
      }
    } catch (error) {
      console.error('Error during confirmation flow:', error);
      this.setMultiPropertyType(previousValue, previousLabel);
    } finally {
      this.isProcessingChange = false;
    }
  }

  applyMultiPropertyTypeChange(newValue, newLabel) {
    const wasPortfolio = this.previousMultiPropertyTypeValue !== 'single_property';
    const isNowSingleProperty = newValue === 'single_property';

    if (isNowSingleProperty && wasPortfolio) {
      // Converting TO single_property: mark extra properties for destruction
      this.markExtraPropertiesForDestruction();
    } else if (!isNowSingleProperty && this.currentAddressEntryMode() === 'upload') {
      if (newValue === 'portfolio_separate_report') {
        // Separate reports leave bulk-upload mode, so restore the properties it replaced.
        this.restoreExtraProperties();
      } else {
        // Returning to a single report resumes bulk-upload mode.
        this.resetExtraProperties();
      }
    } else if (!isNowSingleProperty && this.previousMultiPropertyTypeValue === 'single_property') {
      // Converting FROM single_property TO portfolio: unmark all properties
      this.unmarkPropertiesForDestruction();
    }

    this.togglePortfolioSingleReportSection();
    this.toggleTabControls();
    this.togglePortfolioValueFieldsVisibility();
    this.toggleSpecificPropertyTypeVisibility();
    this.updateSpecificPropertyTypeRequiredMarkers();
    this.syncSpecificPropertyType();
    this.togglePropertyAddressVisibility();
    this.updatePropertyTitles(newValue);

    if (isNowSingleProperty) {
      this.collapseMultiPropertyDetailsSectionIfEmpty();
    } else {
      this.expandMultiPropertyDetailsSection();
    }

    const isPropertyView = this.currentViewValue.startsWith('report-');
    const currentView = isPropertyView ? 'report' : 'valuation-details';
    const reportIndex = isPropertyView ? parseInt(this.currentViewValue.replace('report-', '')) : null;
    this.toggleSidebarSectionsForView(currentView, reportIndex);

    this.previousMultiPropertyTypeValue = newValue;
    this.previousLabel = newLabel;
  }

  markExtraPropertiesForDestruction() {
    this.reportSectionTargets.forEach((section, index) => {
      // Skip first property - it should remain active
      if (index === 0) return;

      this.setDestroyField(section, true);
    });
  }

  unmarkPropertiesForDestruction() {
    this.reportSectionTargets.forEach((section) => {
      this.setDestroyField(section, false);
    });
  }

  setDestroyField(section, value) {
    const destroyField = section.querySelector('input[name*="[_destroy]"]');
    if (destroyField) {
      destroyField.value = value ? '1' : '0';
    }
  }

  getLabelForValue(value) {
    return MULTI_PROPERTY_LABELS[value] || '';
  }

  getPropertyTitle(multiPropertyType = null) {
    const type = multiPropertyType || this.getCurrentMultiPropertyType();
    return type === 'portfolio_separate_report' ? 'Report' : 'Property';
  }

  updatePropertyTitles(multiPropertyType) {
    const title = this.getPropertyTitle(multiPropertyType);

    // Update property section headers
    this.propertyTitleTargets.forEach(el => {
      el.textContent = title;
    });

    // Update report buttons
    this.reportButtonTargets.forEach((button, index) => {
      const labelSpan = button.querySelector('span');
      if (labelSpan) {
        labelSpan.textContent = `${title} №${index + 1}`;
      }
    });
  }

  getPreviousLabel() {
    return this.previousLabel || MULTI_PROPERTY_LABELS[this.previousMultiPropertyTypeValue] || '';
  }

  async showConfirmationModal(fromType, toType) {
    const confirmController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller~="confirm"]'),
      'confirm',
    );

    if (!confirmController) return false;

    const { title, message } = this.getConfirmationContent(fromType, toType);
    return await confirmController.request({ title, message, confirmButton: 'Convert', destructive: true });
  }

  // Carries the currently selected valuation type to the archive page so unarchiving there can
  // convert the valuation to that portfolio type as it restores the property.
  openArchive(event) {
    event.preventDefault();

    const target = new URL(event.currentTarget.href, window.location.origin);
    target.searchParams.set('intended_multi_property_type', this.getCurrentMultiPropertyType());
    window.location.assign(target.toString());
  }

  getConfirmationContent(fromType, toType) {
    return CONVERSION_MODAL_CONTENT[`${fromType}:${toType}`] || { title: '', message: '' };
  }

  togglePortfolioSingleReportSection() {
    if (!this.hasMultiPropertyTypeSelectTarget || !this.hasPortfolioSingleReportSectionTarget) return;

    const selectedValue = this.getCurrentMultiPropertyType();

    const isPortfolioToPortfolio =
      (this.previousMultiPropertyTypeValue === 'portfolio_single_report' || this.previousMultiPropertyTypeValue === 'portfolio_separate_report') &&
      (selectedValue === 'portfolio_single_report' || selectedValue === 'portfolio_separate_report');

    if (isPortfolioToPortfolio) return;

    if (selectedValue === 'single_property') {
      this.portfolioSingleReportSectionTarget.classList.add('hidden');

      if (this.hasValuationDetailsSectionTarget) {
        this.valuationDetailsSectionTarget.classList.remove('hidden');
      }

      this.reportSectionTargets.forEach((section, index) => {
        if (index === 0) {
          section.classList.remove('hidden');
        } else {
          section.classList.add('hidden');
        }
      });

      if (this.reportHeaderTargets.length > 0) {
        this.reportHeaderTargets[0].classList.add('hidden');
      }
    } else {
      this.portfolioSingleReportSectionTarget.classList.remove('hidden');
      this.switchToView('valuation-details');
      if (this.reportHeaderTargets.length > 0) {
        this.reportHeaderTargets[0].classList.remove('hidden');
      }
    }
  }

  togglePortfolioValueFieldsVisibility() {
    const type = this.getCurrentMultiPropertyType();
    const isPortfolio = type === 'portfolio_single_report' || type === 'portfolio_separate_report';
    this.portfolioValueFieldTargets.forEach(el => {
      el.classList.toggle('hidden', !isPortfolio);
    });
    this.updatePropertiesValueSumVisibility();
  }

  updatePropertiesValueSumVisibility() {
    if (!this.hasPropertiesValueSumFieldTarget) return;

    const type = this.getCurrentMultiPropertyType();
    const isPortfolio = type === 'portfolio_single_report' || type === 'portfolio_separate_report';
    const visible = isPortfolio && this.currentAddressEntryMode() !== 'upload';
    this.propertiesValueSumFieldTarget.classList.toggle('hidden', !visible);
  }

  toggleSpecificPropertyTypeVisibility() {
    const type = this.getCurrentMultiPropertyType();
    const isPortfolio = type === 'portfolio_single_report' || type === 'portfolio_separate_report';
    this.specificPropertyTypeFieldTargets.forEach(el => {
      el.classList.toggle('hidden', !isPortfolio);
    });
  }

  updateSpecificPropertyTypeRequiredMarkers() {
    const isSeparateReport = this.getCurrentMultiPropertyType() === 'portfolio_separate_report';

    this.specificPropertyTypeFieldTargets.forEach((field, index) => {
      const required = index === 0 || isSeparateReport;

      const button = field.querySelector('button[data-required]');
      if (button) button.dataset.required = required;

      const labelWrapper = field.querySelector('label')?.parentElement;
      if (!labelWrapper) return;

      let marker = labelWrapper.querySelector('span.text-brand-800');
      if (!marker && required) {
        marker = document.createElement('span');
        marker.className = 'text-brand-800';
        marker.textContent = '*';
        labelWrapper.appendChild(marker);
      }
      if (marker) marker.classList.toggle('hidden', !required);
    });
  }

  initialiseSyncedPropertyType() {
    const vpValue = this.vpPropertyTypeValue();
    const propertyValue = this.primaryPropertyTypeValue();
    this.lastSyncedPropertyTypeValue = vpValue && vpValue === propertyValue ? vpValue : null;
  }

  syncSpecificPropertyType() {
    if (!this.isPortfolioType()) return;

    const vpValue = this.vpPropertyTypeValue();
    if (!vpValue) return;

    const propertyValue = this.primaryPropertyTypeValue();
    if (propertyValue && propertyValue !== vpValue && propertyValue !== this.lastSyncedPropertyTypeValue) return;

    this.applySyncedPropertyType(this.primaryPropertyTypeSelectElement(), vpValue);
  }

  syncValuationPropertyType(event) {
    if (!this.isPortfolioType()) return;
    if (!this.primaryPropertyTypeSelectElement()?.contains(event.target)) return;

    const propertyValue = this.primaryPropertyTypeValue();
    if (!propertyValue) return;

    this.applySyncedPropertyType(this.vpPropertyTypeSelectElement(), propertyValue);
  }

  applySyncedPropertyType(selectElement, value) {
    if (!selectElement) return;
    if (this.selectedPropertyTypeValue(selectElement) === value) {
      this.lastSyncedPropertyTypeValue = value;
      return;
    }

    const selectController = this.application.getControllerForElementAndIdentifier(
      selectElement,
      'single-select-field-component-with-search'
    );
    if (!selectController) return;

    selectController.selectItem(value);
    this.lastSyncedPropertyTypeValue = value;
  }

  isPortfolioType() {
    const type = this.getCurrentMultiPropertyType();
    return type === 'portfolio_single_report' || type === 'portfolio_separate_report';
  }

  vpPropertyTypeValue() {
    return this.selectedPropertyTypeValue(this.vpPropertyTypeSelectElement());
  }

  vpPropertyTypeSelectElement() {
    const field = this.element.querySelector('input[name="valuation[property_type_id]"]');
    return field?.closest('[data-controller~="single-select-field-component-with-search"]') || null;
  }

  primaryPropertyTypeValue() {
    return this.selectedPropertyTypeValue(this.primaryPropertyTypeSelectElement());
  }

  primaryPropertyTypeSelectElement() {
    const wrapper = this.specificPropertyTypeFieldTargets[0];
    return wrapper?.querySelector('[data-controller~="single-select-field-component-with-search"]') || null;
  }

  selectedPropertyTypeValue(selectElement) {
    const field = selectElement?.querySelector('input[type="hidden"]');
    return (field?.value || '').trim();
  }

  expandMultiPropertyDetailsSection() {
    if (!this.hasMultiPropertyDetailsSectionTarget) return;

    this.multiPropertyDetailsSectionTarget.dispatchEvent(new CustomEvent('collapsable-section:expand', { bubbles: false }));
  }

  collapseMultiPropertyDetailsSectionIfEmpty() {
    if (!this.hasMultiPropertyDetailsSectionTarget) return;

    if (this.isMultiPropertySectionEmpty()) {
      this.multiPropertyDetailsSectionTarget.dispatchEvent(new CustomEvent('collapsable-section:collapse', { bubbles: false }));
    }
  }

  isMultiPropertySectionEmpty() {
    const propertyCountField = this.element.querySelector('[name="valuation[portfolio_property_count]"]');
    const valuationBasisField = this.element.querySelector('[name="valuation[portfolio_valuation_basis]"]');
    const descriptionField = this.element.querySelector('[name="valuation[portfolio_description]"]');
    const portfolioValueField = this.element.querySelector('[name="valuation[value]"]');
    const propertiesValueSumField = this.element.querySelector('[name="valuation[properties_value_sum]"]');
    const annualIncomeField = this.hasMultiPropertyDetailsSectionTarget
      ? this.multiPropertyDetailsSectionTarget.querySelector('[name="valuation[annual_rent]"]')
      : null;

    const propertyCountEmpty = !propertyCountField?.value?.trim();
    const valuationBasisEmpty = !valuationBasisField?.value?.trim();
    const descriptionEmpty = !descriptionField?.value?.trim();
    const portfolioValueEmpty = !portfolioValueField?.value?.trim();
    const propertiesValueSumEmpty = !propertiesValueSumField?.value?.trim();
    const annualIncomeEmpty = !annualIncomeField?.value?.trim();

    return propertyCountEmpty && valuationBasisEmpty && descriptionEmpty && portfolioValueEmpty &&
      propertiesValueSumEmpty && annualIncomeEmpty;
  }

  togglePropertyAddressVisibility() {
    if (!this.hasMultiPropertyTypeSelectTarget) return;

    const isHidden = this.getCurrentMultiPropertyType() !== 'single_property';

    const targets = [
      this.hasPropertyAddressNavButtonTarget && this.propertyAddressNavButtonTarget,
      this.hasPropertyAddressSectionTarget && this.propertyAddressSectionTarget,
      this.hasPropertyDetailsNavButtonTarget && this.propertyDetailsNavButtonTarget,
      this.hasPropertyDetailsSectionTarget && this.propertyDetailsSectionTarget,
    ].filter(Boolean);

    targets.forEach(el => el.classList.toggle('hidden', isHidden));
  }

  toggleSidebarSectionsForView(viewType, reportIndex = null) {
    const isPortfolioMode = this.getCurrentMultiPropertyType() !== 'single_property';
    const isPropertyView = viewType !== 'valuation-details';

    this.propertySidebarSectionTargets.forEach(section => {
      const sectionIndex = parseInt(section.dataset.reportIndex);
      const targetIndex = isPortfolioMode ? reportIndex : 0;
      const shouldShow = isPropertyView || !isPortfolioMode;
      section.classList.toggle('hidden', !shouldShow || sectionIndex !== targetIndex);
    });

    this.valuationDetailsSidebarSectionTargets.forEach(section => {
      section.classList.toggle('hidden', isPortfolioMode && isPropertyView);
    });
  }

  showValuationDetails(event) {
    event?.preventDefault();
    this.switchToView('valuation-details');
  }

  showReport(event) {
    event?.preventDefault();
    const reportIndex = parseInt(event.currentTarget.dataset.reportIndex);
    this.switchToView('report', reportIndex);
  }

  switchToView(viewType, reportIndex = null) {
    this.currentViewValue = viewType === 'valuation-details' ? 'valuation-details' : `report-${reportIndex}`;
    if (viewType === 'valuation-details') {
      this.showValuationDetailsSection();
      this.hideAllReportSections();
    } else {
      this.hideValuationDetailsSection();
      this.showOnlyReportSection(reportIndex);
    }
    this.updateButtonStates();
    this.toggleSidebarSectionsForView(viewType, reportIndex);
  }

  showValuationDetailsSection() {
    if (this.hasValuationDetailsSectionTarget) {
      this.valuationDetailsSectionTarget.classList.remove('hidden');
    }
  }

  hideValuationDetailsSection() {
    if (this.hasValuationDetailsSectionTarget) {
      this.valuationDetailsSectionTarget.classList.add('hidden');
    }
  }

  hideAllReportSections() {
    this.reportSectionTargets.forEach(report => report.classList.add('hidden'));
  }

  showOnlyReportSection(index) {
    this.reportSectionTargets.forEach((report, i) => report.classList.toggle('hidden', i !== index));
  }

  updateButtonStates() {
    if (this.hasValuationDetailsButtonTarget) {
      this.setButtonState(this.valuationDetailsButtonTarget, this.currentViewValue === 'valuation-details');
    }

    this.reportButtonTargets.forEach((button, index) => {
      this.setButtonState(button, this.currentViewValue === `report-${index}`);
    });
  }

  setButtonState(button, isActive) {
    if (isActive) {
      this.setButtonActive(button);
    } else {
      this.setButtonInactive(button);
    }
  }

  setButtonActive(button) {
    button.classList.remove(...BUTTON_INACTIVE_CLASSES);
    button.classList.add(...BUTTON_ACTIVE_CLASSES);
  }

  setButtonInactive(button) {
    button.classList.remove(...BUTTON_ACTIVE_CLASSES);
    button.classList.add(...BUTTON_INACTIVE_CLASSES);
  }

  addMoreReport(event) {
    event?.preventDefault();

    const nestedFormContainer = this.element.querySelector('[data-controller*="rails-nested-form"]');
    if (nestedFormContainer) {
      const nestedFormController = this.application.getControllerForElementAndIdentifier(
        nestedFormContainer,
        'rails-nested-form'
      );
      if (nestedFormController) {
        nestedFormController.add(event || new CustomEvent('rails-nested-form:add'));
      }
    }
  }

  // Stimulus lifecycle callback - called when a new reportSection target is connected
  reportSectionTargetConnected(element) {
    // Only create button for newly added sections (not on initial page load)
    const currentCount = this.reportSectionTargets.length;
    if (currentCount > this.initialReportCount) {
      const newIndex = currentCount - 1;
      // Sections marked for destruction stay in the DOM but must not count towards the
      // displayed number, otherwise a new property after a reset is labelled №3 with №2 hidden.
      const visibleSections = this.reportSectionTargets.filter(section => this.isReportSectionVisible(section));
      const displayIndex = visibleSections.indexOf(element);
      this.updatePropertyHeader(element, displayIndex);
      this.updatePropertySectionIds(element, newIndex);
      this.createPropertyNavButtons(newIndex);
      this.refreshScrollToSectionController();
      this.createReportButton(newIndex, displayIndex);
      this.initialReportCount = currentCount;

      const currentType = this.getCurrentMultiPropertyType();
      if (currentType === 'portfolio_single_report' || currentType === 'portfolio_separate_report') {
        const field = element.querySelector('[data-multi-property-target="specificPropertyTypeField"]');
        if (field) field.classList.remove('hidden');
      }
      this.updateSpecificPropertyTypeRequiredMarkers();
    }
    this.updateAddMoreButtonState();
  }

  // Stimulus lifecycle callback - called when a reportSection target is disconnected
  reportSectionTargetDisconnected(element) {
    this.pendingTimeoutId = setTimeout(() => {
      this.pendingTimeoutId = null;
      const visibleSections = this.reportSectionTargets.filter(section => this.isReportSectionVisible(section));

      // Update headers and section IDs for all visible sections with correct sequential indices
      this.renumberPropertyHeaders(visibleSections);
      this.renumberPropertySectionIds(visibleSections);

      // Remove nav buttons that no longer have matching sections
      this.removeOrphanedPropertyNavButtons();
      this.rebuildPropertyButtons();
      this.refreshScrollToSectionController();
      this.switchToView('valuation-details');
      this.updateAddMoreButtonState();
      this.ensureSinglePropertyButtonsVisible();
    }, 0);
  }

  removeOrphanedPropertyNavButtons() {
    const scrollToSectionNav = document.querySelector('[data-controller="scroll-to-section"]');
    if (!scrollToSectionNav) return;

    const navButtons = scrollToSectionNav.querySelectorAll('[data-multi-property-target="propertySidebarSection"][data-report-index]');
    navButtons.forEach(btn => {
      const index = btn.dataset.reportIndex;
      const sectionExists = document.querySelector(`section[id="property_address_${index}"]`);
      if (!sectionExists) {
        btn.remove();
      }
    });
  }

  updatePropertyHeader(element, index) {
    const propertyTitle = element.querySelector('[data-multi-property-target="propertyTitle"]');
    if (propertyTitle) propertyTitle.textContent = this.getPropertyTitle();

    const propertyNumber = element.querySelector('[data-property-number]');
    if (propertyNumber) propertyNumber.textContent = ` №${index + 1}`;
  }

  get propertyButtonAnchor() {
    if (this.hasAddMoreContainerTarget) return this.addMoreContainerTarget;
    if (this.hasAddMoreButtonTarget) return this.addMoreButtonTarget;
    return this.hasActionsMenuTarget ? this.actionsMenuTarget : null;
  }

  toggleTabControls() {
    const isSingleReport = this.getCurrentMultiPropertyType() === 'portfolio_single_report';
    if (this.hasAddMoreContainerTarget) {
      this.addMoreContainerTarget.classList.toggle('hidden', isSingleReport);
    }
    if (this.hasActionsMenuContainerTarget) {
      this.actionsMenuContainerTarget.classList.toggle('hidden', !isSingleReport);
    }
    if (this.hasAdditionalAddressesUploadTarget) {
      this.additionalAddressesUploadTarget.classList.toggle('inline-flex', isSingleReport);
      this.additionalAddressesUploadTarget.classList.toggle('hidden', !isSingleReport);
    }
  }

  createReportButton(index, displayNumber = index) {
    if (!this.hasReportButtonTemplateTarget) return;

    const buttonContainer = this.portfolioSingleReportSectionTarget.querySelector('.flex.gap-8');
    const anchor = this.propertyButtonAnchor;

    if (!buttonContainer || !anchor) return;

    const template = this.reportButtonTemplateTarget;
    const clone = template.content.cloneNode(true);
    const newButton = clone.querySelector('button');

    newButton.innerHTML = newButton.innerHTML
      .replace(/__PROPERTY_TITLE__/g, this.getPropertyTitle())
      .replace(/__INDEX__/g, displayNumber + 1);
    newButton.dataset.reportIndex = index;

    buttonContainer.insertBefore(newButton, anchor);
    this.switchToView('report', index);
  }

  isReportSectionVisible(section) {
    const destroyField = section.querySelector('input[name*="[_destroy]"]');

    return !(destroyField && destroyField.value === '1');
  }

  rebuildPropertyButtons() {
    if (!this.hasPortfolioSingleReportSectionTarget) return;

    const buttonContainer = this.portfolioSingleReportSectionTarget.querySelector('.flex.gap-8');
    if (!buttonContainer) return;

    const propertyButtons = buttonContainer.querySelectorAll('[data-multi-property-target="reportButton"]');
    propertyButtons.forEach(btn => btn.remove());
    const visibleSections = this.reportSectionTargets.filter(section => this.isReportSectionVisible(section));

    visibleSections.forEach((section, index) => {
      if (!this.hasReportButtonTemplateTarget) return;

      const anchor = this.propertyButtonAnchor;
      if (!anchor) return;

      const template = this.reportButtonTemplateTarget;
      const clone = template.content.cloneNode(true);
      const newButton = clone.querySelector('button');

      newButton.innerHTML = newButton.innerHTML
        .replace(/__PROPERTY_TITLE__/g, this.getPropertyTitle())
        .replace(/__INDEX__/g, index + 1);
      newButton.dataset.reportIndex = this.reportSectionTargets.indexOf(section);

      buttonContainer.insertBefore(newButton, anchor);
    });
    this.initialReportCount = visibleSections.length;
  }

  updateAddMoreButtonState() {
    if (!this.hasAddMoreButtonTarget) return;

    const visibleCount = this.reportSectionTargets.filter(section => this.isReportSectionVisible(section)).length;
    const isAtLimit = visibleCount >= MAX_PROPERTIES;

    this.addMoreButtonTarget.disabled = isAtLimit;
    this.addMoreButtonTarget.classList.toggle('cursor-not-allowed', isAtLimit);
    this.addMoreButtonTarget.classList.toggle('opacity-50', isAtLimit);
  }

  renumberPropertyHeaders(visibleSections) {
    visibleSections.forEach((section, index) => {
      this.updatePropertyHeader(section, index);
    });
  }

  renumberPropertySectionIds(visibleSections) {
    visibleSections.forEach((section, index) => {
      const propertyAddressSection = section.querySelector('section[id^="property_address"]');
      const propertyDetailsSection = section.querySelector('section[id^="property_details"]');

      if (propertyAddressSection) {
        propertyAddressSection.id = `property_address_${index}`;
      }
      if (propertyDetailsSection) {
        propertyDetailsSection.id = `property_details_${index}`;
      }
    });
  }

  updatePropertySectionIds(element, index) {
    const propertyAddressSection = element.querySelector('section[id="property_address"]');
    const propertyDetailsSection = element.querySelector('section[id="property_details"]');

    if (propertyAddressSection) {
      propertyAddressSection.id = `property_address_${index}`;
    }
    if (propertyDetailsSection) {
      propertyDetailsSection.id = `property_details_${index}`;
    }
  }

  createPropertyNavButtons(index) {
    const scrollToSectionNav = document.querySelector('[data-controller="scroll-to-section"]');
    if (!scrollToSectionNav) return;

    const template = scrollToSectionNav.querySelector('template[data-multi-property-target="propertyNavButtonTemplate"]');
    if (!template) return;

    const clone = template.content.cloneNode(true);

    const buttons = clone.querySelectorAll('button');
    buttons.forEach(button => {
      const sectionIdParam = button.dataset.scrollToSectionIdParam;
      if (sectionIdParam) {
        button.dataset.scrollToSectionIdParam = sectionIdParam.replace(/__INDEX__/g, index);
      }
      button.dataset.reportIndex = index;
    });

    const firstNonPropertyButton = scrollToSectionNav.querySelector('[data-multi-property-target="valuationDetailsSidebarSection"]');
    if (firstNonPropertyButton) {
      firstNonPropertyButton.parentNode.insertBefore(clone, firstNonPropertyButton);
    } else {
      scrollToSectionNav.appendChild(clone);
    }
  }

  refreshScrollToSectionController() {
    const scrollToSectionNav = document.querySelector('[data-controller="scroll-to-section"]');
    if (!scrollToSectionNav) return;

    const scrollController = this.application.getControllerForElementAndIdentifier(
      scrollToSectionNav,
      'scroll-to-section'
    );

    if (scrollController && typeof scrollController.refreshSections === 'function') {
      scrollController.refreshSections();
    }
  }

  // === Portfolio single-report Actions menu ===
  onActionsMenuChange(event) {
    const value = event.detail?.value;
    this.restoreActionsMenu(event.target);
    if (!value) return;

    switch (value) {
      case 'enter_individually':
        this.enterIndividualMode();
        break;
      case 'add_new_property':
        this.addPropertyFromMenu();
        break;
      case 'reset_selection':
        this.resetSelection();
        break;
      case 'upload_list':
        this.enterUploadMode();
        break;
      case 'upload_file':
        this.openAddressUploadModal();
        break;
    }
  }

  restoreActionsMenu(target) {
    const menuEl = target?.closest?.('[data-controller~="menu"]');
    if (!menuEl) return;

    const menuController = this.application.getControllerForElementAndIdentifier(menuEl, 'menu');
    if (!menuController) return;

    menuController.selectedValueValue = '';
    if (menuController.hasLabelTarget) {
      menuController.labelTarget.textContent = menuController.buttonTarget?.dataset.defaultLabel || 'Actions';
    }
  }

  currentAddressEntryMode() {
    return this.hasAddressEntryModeFieldTarget ? this.addressEntryModeFieldTarget.value : '';
  }

  setAddressEntryMode(value) {
    if (this.hasAddressEntryModeFieldTarget) {
      this.addressEntryModeFieldTarget.value = value || '';
    }
  }

  showActionsMenu(variant) {
    this.actionsMenuVariantTargets.forEach(el => {
      el.classList.toggle('hidden', el.dataset.variant !== variant);
    });
  }

  enterIndividualMode() {
    this.setAddressEntryMode('individual');
    this.updatePropertiesValueSumVisibility();
    this.showActionsMenu('manual');
    this.addMoreReport();
    this.updateManualMenuCap();
  }

  addPropertyFromMenu() {
    const visibleCount = this.reportSectionTargets.filter(section => this.isReportSectionVisible(section)).length;
    if (visibleCount >= MAX_PROPERTIES) return;

    this.addMoreReport();
    this.updateManualMenuCap();
  }

  resetSelection() {
    if (this.currentAddressEntryMode() === 'upload') {
      this.removeAdditionalAddressesFile();
      this.restoreExtraProperties();
    } else {
      this.resetExtraProperties();
    }

    this.setAddressEntryMode('');
    this.updatePropertiesValueSumVisibility();
    this.showActionsMenu('initial');
  }

  enterUploadMode() {
    this.setAddressEntryMode('upload');
    this.updatePropertiesValueSumVisibility();
    this.resetExtraProperties();
    this.showActionsMenu('upload');
    this.openAddressUploadModal();
  }

  openAddressUploadModal() {
    const id = 'additional_addresses_file_picker-upload-modal';
    const host = this.hasAdditionalAddressesModalTarget ? this.additionalAddressesModalTarget : this.element;
    host.dispatchEvent(new CustomEvent('local-modal-manager:open', { bubbles: true, detail: { id } }));
  }

  onAddressFileConfirm(event) {
    if (event.detail?.fieldName !== 'valuation[additional_addresses_file_picker]') return;

    const files = Array.from(event.detail?.files || []);
    if (!files.length || !this.hasAdditionalAddressesInputTarget) return;

    const file = files[0];
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    this.additionalAddressesInputTarget.files = dataTransfer.files;
    this.additionalAddressesInputTarget.disabled = false;

    this.disableAdditionalAddressesSignedIdField();
    this.setRemoveAdditionalAddressesFile(false);
    this.renderAdditionalAddressesFile(file.name);
    this.setUploadMenuLabel('Replace File');
  }

  deleteAdditionalAddressesFile(event) {
    event?.preventDefault();
    this.removeAdditionalAddressesFile();
  }

  removeAdditionalAddressesFile() {
    if (this.hasAdditionalAddressesInputTarget) {
      this.additionalAddressesInputTarget.value = '';
      this.additionalAddressesInputTarget.disabled = true;
    }

    this.disableAdditionalAddressesSignedIdField();
    this.setRemoveAdditionalAddressesFile(true);

    if (this.hasAdditionalAddressesDisplayTarget) {
      this.additionalAddressesDisplayTarget.innerHTML = '';
      this.additionalAddressesDisplayTarget.classList.add('hidden');
    }

    this.setUploadMenuLabel('Upload File');
  }

  renderAdditionalAddressesFile(filename) {
    if (!this.hasAdditionalAddressesDisplayTarget || !this.hasAdditionalAddressesRowTemplateTarget) return;

    const clone = this.additionalAddressesRowTemplateTarget.content.cloneNode(true);
    const nameEl = clone.querySelector('[data-file-name]');
    if (nameEl) nameEl.textContent = filename;

    const extEl = clone.querySelector('[data-file-ext]');
    if (extEl) {
      const parts = filename.split('.');
      const ext = parts.length > 1 ? parts.pop() : '';
      extEl.textContent = ext.slice(0, 3).toUpperCase() || 'FILE';
    }

    this.additionalAddressesDisplayTarget.innerHTML = '';
    this.additionalAddressesDisplayTarget.appendChild(clone);
    this.additionalAddressesDisplayTarget.classList.remove('hidden');
  }

  disableAdditionalAddressesSignedIdField() {
    if (this.hasAdditionalAddressesSignedIdFieldTarget) {
      this.additionalAddressesSignedIdFieldTarget.disabled = true;
    }
  }

  setRemoveAdditionalAddressesFile(value) {
    if (this.hasRemoveAdditionalAddressesFileFieldTarget) {
      this.removeAdditionalAddressesFileFieldTarget.value = value ? '1' : '0';
    }
  }

  setUploadMenuLabel(label) {
    const menuController = this.menuControllerForVariant('upload');
    const item = menuController?.dropdownEl?.querySelector('li[data-value="upload_file"]');
    if (!item) return;

    item.dataset.label = label;
    const labelEl = item.querySelector('[data-label]');
    if (labelEl) labelEl.textContent = label;
  }

  restoreExtraProperties() {
    this.unmarkPropertiesForDestruction();
    this.rebuildPropertyButtons();
    this.refreshScrollToSectionController();
    this.switchToView('valuation-details');
  }

  resetExtraProperties() {
    Array.from(this.reportSectionTargets).forEach((section, index) => {
      if (index === 0) return;

      if (section.dataset.newRecord === 'true') {
        section.remove();
      } else {
        this.setDestroyField(section, true);
        section.classList.add('hidden');
      }
    });

    this.rebuildPropertyButtons();
    this.removeOrphanedPropertyNavButtons();
    this.refreshScrollToSectionController();
    this.switchToView('valuation-details');
  }

  updateManualMenuCap() {
    const visibleCount = this.reportSectionTargets.filter(section => this.isReportSectionVisible(section)).length;
    const atCap = visibleCount >= MAX_PROPERTIES;

    const menuController = this.menuControllerForVariant('manual');
    const addItem = menuController?.dropdownEl?.querySelector('li[data-value="add_new_property"]');
    if (!addItem) return;

    addItem.classList.toggle('opacity-50', atCap);
  }

  menuControllerForVariant(variant) {
    const wrapper = this.actionsMenuVariantTargets.find(el => el.dataset.variant === variant);
    const menuEl = wrapper?.querySelector('[data-controller~="menu"]');
    if (!menuEl) return null;

    return this.application.getControllerForElementAndIdentifier(menuEl, 'menu');
  }
}
